import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { t } from '../../../../lib/i18n';
import { pluralUk } from '../../../finance/utils';
import { formatChf } from './finance-format';
import { fetchUserPayments } from '../../api';
import { formatShortDate } from '../../utils';

/**
 * «▸ Архів (стара система) · N записів» — the legacy `clientPayments` rows,
 * collapsed and READ-ONLY (their writes answer 410). They never moved the
 * deposit or an invoice; they stay visible so nothing an operator once typed
 * disappears. The rows are fetched only when the section is expanded.
 */
export function LegacyArchive({ userId, count }: { userId: string; count: number }) {
  const [expanded, setExpanded] = useState(false);
  const query = useQuery({
    queryKey: ['user', userId, 'payments'],
    queryFn: () => fetchUserPayments(userId),
    enabled: expanded,
  });

  if (count === 0) return null;

  const records = pluralUk(count, {
    one: t('clients.finance.archive.records.one'),
    few: t('clients.finance.archive.records.few'),
    many: t('clients.finance.archive.records.many'),
  });

  return (
    <details
      data-testid="finance-archive"
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-3 lg:px-6"
    >
      <summary className="cursor-pointer text-[14px] font-semibold text-cf-ink-muted focus-visible:outline-2 focus-visible:outline-cf-ink">
        {t('clients.finance.archive.summary', { count: String(count), records })}
      </summary>
      <div className="mt-3 flex flex-col gap-2">
        <p className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle">
          {t('clients.finance.archive.hint')}
        </p>
        {query.isPending && expanded ? (
          <p className="m-0 text-[13px] text-cf-ink-subtle">
            {t('clients.finance.archive.loading')}
          </p>
        ) : query.isError ? (
          <p className="m-0 text-[13px] text-cf-danger">{t('clients.finance.archive.failed')}</p>
        ) : (
          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="border-b border-cf-line text-[12px] text-cf-ink-subtle">
                {(['date', 'amount', 'method', 'notes'] as const).map((column) => (
                  <th key={column} scope="col" className="py-1.5 pr-3 font-normal">
                    {t(`clients.finance.archive.columns.${column}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(query.data ?? []).map((payment, index) => (
                <tr
                  key={payment._id ?? index}
                  data-testid="finance-archive-row"
                  className="border-b border-cf-line-faint text-cf-ink-muted last:border-b-0"
                >
                  <td className="py-1.5 pr-3 font-mono">{formatShortDate(payment.paymentDate)}</td>
                  <td className="py-1.5 pr-3 font-mono">{formatChf(payment.amount)}</td>
                  <td className="py-1.5 pr-3">{payment.paymentMethod ?? '—'}</td>
                  <td className="py-1.5 pr-3">{payment.notes ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}
